import { Button } from '@/components/ui/button';
import { ShieldCheck, Globe2, Layers, FileCheck2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import reportMockup from '@/assets/field-intelligence-report-mockup.png';

// Served from /public so the URL is stable and matches the <link rel="preload"> in index.html.
// This lets the browser start downloading the LCP image before JS parses.
const reportMockupWebp1280 = '/images/hero/report-1280.webp';
const reportMockupWebp640 = '/images/hero/report-640.webp';

const HeroSection = () => {
  const { t } = useTranslation();
  const trustChips = [
    { icon: FileCheck2, label: t('hero.chipEvidence') },
    { icon: Globe2, label: t('hero.chipSentinel') },
    { icon: Layers, label: t('hero.chipFao') },
    { icon: ShieldCheck, label: t('hero.chipReadiness') },
  ];

  return (
    <section className="relative flex items-center overflow-hidden bg-[#0B0F0E]">
      {/* Subtle technical grid + radial glow */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#2FB46D]/[0.09] blur-3xl" />
        <div className="absolute -bottom-40 right-0 w-[480px] h-[480px] rounded-full bg-amber-500/[0.05] blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-20 container mx-auto px-4 sm:px-6 pt-24 pb-4 md:pt-28 md:pb-6">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left column — copy */}
          <motion.div
            className="lg:col-span-5 text-left"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
           {/* Eyebrow */}
           <motion.span
             className="inline-block px-3 py-1 mb-5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[11px] font-semibold tracking-[0.18em] uppercase text-white/90"
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: 0.15, duration: 0.5 }}
           >
             {t('hero.eyebrow')}
           </motion.span>

           {/* Main Heading */}
           <motion.h1 
             className="text-4xl sm:text-5xl lg:text-[3.2rem] xl:text-[3.6rem] font-semibold leading-[1.05] tracking-tight mb-5 text-white"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.2, duration: 0.6 }}
           >
              {t('hero.title')}
            </motion.h1>

          {/* Subheading */}
          <motion.p 
            className="text-base md:text-[17px] text-white/75 mb-8 leading-relaxed max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.p
            className="-mt-4 mb-8 text-sm md:text-[15px] font-medium text-[#2FB46D]/90 max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38, duration: 0.6 }}
          >
            {t('hero.secondary')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col items-start gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.6 }}
          >
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center w-full sm:w-auto">
              <Button
                size="lg"
                asChild
                className="w-full sm:w-auto bg-[#2FB46D] hover:bg-[#27975C] text-white px-7 py-5 rounded-lg font-semibold shadow-xl shadow-[#2FB46D]/20 transition-all"
              >
                <a
                  href="https://ultknizonvqwrnhkblwm.supabase.co/storage/v1/object/public/marketing-assets/sample-report.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View a sample Field Intelligence Report (PDF)"
                >
                  {t('hero.viewSampleReport')}
                </a>
              </Button>

              <a
                href="https://calendly.com/jose-sentinelcrop/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
                aria-label="Book a demo with our team"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto border border-white/40 bg-white/5 backdrop-blur text-white hover:bg-white hover:text-neutral-900 px-7 py-5 rounded-lg font-semibold transition-all"
                >
                  {t('hero.bookDemo')}
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Trust chips */}
          <motion.div
            className="mt-8 pt-6 border-t border-white/10 grid grid-cols-2 gap-x-5 gap-y-3 text-white/65 max-w-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            {trustChips.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-[12px] font-medium">
                <Icon className="w-3.5 h-3.5 shrink-0 text-[#2FB46D]" aria-hidden="true" />
                <span>{label}</span>
              </div>
            ))}
          </motion.div>
          </motion.div>

          {/* Right column — product mockup */}
          <motion.div
            className="lg:col-span-7 relative"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25, ease: 'easeOut' }}
          >
            {/* Glow behind */}
            <div className="absolute -inset-6 bg-gradient-to-br from-[#2FB46D]/20 via-emerald-500/10 to-transparent blur-3xl rounded-3xl" aria-hidden="true" />

            {/* Floating mockup */}
            <div className="relative rounded-xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/60">
              <picture>
                <source
                  type="image/webp"
                  srcSet={`${reportMockupWebp640} 640w, ${reportMockupWebp1280} 1280w`}
                  sizes="(max-width: 768px) 100vw, 60vw"
                />
                <img
                  src={reportMockup}
                  alt="Sample Sentinel Crop Field Intelligence Report for an olive grove parcel, showing NDVI summary, FAO-56 water demand, data availability, agronomic recommendations, NDVI map and a decision-support disclaimer."
                  className="w-full h-auto block"
                  width={1280}
                  height={896}
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </div>

            {/* Floating annotation chip */}
            <motion.div
              className="hidden md:flex absolute -left-4 lg:-left-8 top-1/3 items-center gap-2 px-3 py-2 rounded-lg bg-neutral-900/90 backdrop-blur ring-1 ring-white/10 shadow-xl"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1, duration: 0.5 }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2FB46D] animate-pulse" />
              <span className="text-[11px] font-mono uppercase tracking-wider text-white/80">Live · Sentinel-2</span>
            </motion.div>

            <motion.div
              className="hidden md:flex absolute -right-4 lg:-right-6 bottom-10 flex-col px-3 py-2 rounded-lg bg-neutral-900/90 backdrop-blur ring-1 ring-white/10 shadow-xl"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.3, duration: 0.5 }}
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-white/50">FAO-56</span>
              <span className="text-sm font-semibold text-white">{t('hero.faoBadge')}</span>
            </motion.div>

            {/* Operational outcome overlays */}
            <motion.div
              className="hidden md:flex absolute left-3 lg:left-6 top-6 flex-col px-3 py-2 rounded-lg bg-neutral-900/90 backdrop-blur ring-1 ring-[#2FB46D]/40 shadow-xl"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.45, duration: 0.5 }}
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-[#2FB46D]">Action</span>
              <span className="text-sm font-semibold text-white">{t('hero.overlayIrrigationPriority')}</span>
            </motion.div>

            <motion.div
              className="hidden md:flex absolute right-3 lg:right-6 top-6 flex-col px-3 py-2 rounded-lg bg-neutral-900/90 backdrop-blur ring-1 ring-amber-500/40 shadow-xl"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.55, duration: 0.5 }}
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-amber-400">Risk</span>
              <span className="text-sm font-semibold text-white">{t('hero.overlayWaterStress')}</span>
            </motion.div>

            <motion.div
              className="hidden md:flex absolute left-3 lg:left-6 bottom-6 flex-col px-3 py-2 rounded-lg bg-neutral-900/90 backdrop-blur ring-1 ring-white/10 shadow-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.65, duration: 0.5 }}
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-white/50">Task</span>
              <span className="text-sm font-semibold text-white">{t('hero.overlayFieldTask')}</span>
            </motion.div>

            <motion.div
              className="hidden md:flex absolute right-3 lg:right-6 bottom-28 flex-col px-3 py-2 rounded-lg bg-neutral-900/90 backdrop-blur ring-1 ring-emerald-500/40 shadow-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.75, duration: 0.5 }}
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">Report</span>
              <span className="text-sm font-semibold text-white">{t('hero.overlayVerifiedReport')}</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
